/**
 * TanStack Query client configuration.
 *
 * Single shared QueryClient instance for the application.
 * Retry policy: once on network errors, never on 4xx client errors.
 * Stale time: 30 seconds for most queries (reduces refetch churn on navigation).
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api-client';

/**
 * Determines whether a failed query should be retried.
 * Retries once on network/server errors; never retries 4xx client errors.
 *
 * @param count - The current retry attempt count (0-indexed)
 * @param error - The error thrown by the query function
 * @returns true if the query should be retried
 */
function shouldRetry(count: number, error: unknown): boolean {
  if (count >= 1) return false;
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return true;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: shouldRetry,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});
